<template>
  <div id="app">
    <div v-if="isNavigating" class="nav-loading-bar">
      <div class="nav-loading-bar__fill"/>
    </div>
    <router-view :key="$route.fullPath"/>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useCurrentPreferences } from './composables/useCurrentPreferences';

useCurrentPreferences();

const router = useRouter();
const isNavigating = ref(false);

let navTimer: ReturnType<typeof setTimeout> | null = null;

router.beforeEach(() => {
  if (navTimer) clearTimeout(navTimer);
  isNavigating.value = true;
});

router.afterEach(() => {
  // Small delay so the bar is visible even on fast loads
  navTimer = setTimeout(() => {
    isNavigating.value = false;
  }, 150);
});

// After first mount, prefetch the most-visited lazy-loaded chunks in background
onMounted(() => {
  setTimeout(() => {
    void import('./ui/pages/auth/LoginPage.vue');
    void import('./ui/pages/intro/IntroScreen.vue');
    void import('./ui/pages/user-categories/MySettingsPage.vue');
    void import('./ui/pages/word-collection/WordCollectionPage.vue');
    void import('./ui/pages/daily-words/DailyWordsPage.vue');
    void import('./ui/pages/repeat/repeat-words-page.vue');
    void import('./ui/quizz/QuizPopupExample.vue');
  }, 2000);
});
</script>

<style lang="less">
@import "../src/ui/styles/global.less";
@import "../src/ui/styles/text-styles.less";

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}


body {
  font-family: main-font, -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
  'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
  sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

#app {
  min-height: 100vh;
  width: 100%;
}

.nav-loading-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.06);

  &__fill {
    height: 100%;
    background: #4a90e2;
    animation: nav-loading 1.2s ease-in-out infinite;
    transform-origin: left;
  }
}

@keyframes nav-loading {
  0%   { transform: scaleX(0); margin-left: 0; }
  50%  { transform: scaleX(0.6); margin-left: 0; }
  100% { transform: scaleX(0); margin-left: 100%; }
}
</style>
